import { CUSTOM, FLAT_RATE, FLAT_RATE_STORE_CREDIT, REFUND, RESHIP, STORE_CREDIT } from '../web/pulse/constants/claims';
import { Claims, Merchant, Orders } from '@database/types';
import type { ResolutionAmountBreakdown, ResolutionAmountBreakdownLine } from '@monolog/shared/types';
import isEmpty from 'lodash/isEmpty';
import { matchesVariant } from './orders.helper';

/**
 * Local ResolutionLineDto interface using Claims.* types from @database/types.
 * This avoids cross-boundary imports from claims-svc that depend on gitignored Prisma clients.
 */
interface ResolutionLineDto {
  lineType: Claims.ClaimResolutionLineType;
  resolutionType: string;
  calculationType: Claims.CalculationType;
  claimType: Claims.ClaimType;
  lineItemId: string | null;
  quantity: number;
  percentage: number;
  resolvedAmount: number;
  creditStore: boolean;
  claimItem?: Claims.ClaimItem & { price?: number; discount?: number; tax?: number };
}

export function calculateCredit(lines: Claims.ClaimResolutionLine[]): number {
  return +lines
    .filter(line => isStoreCreditResolution(line.resolutionType))
    .reduce((total, line) => total + line.resolvedAmount, 0)
    .toFixed(2);
}

/**
 * What the customer gets back as money: the refund lines, plus a CUSTOM-typed shipping line when the
 * resolution refunds anything — on a split the shipping line carries the record's type, not the instrument's.
 */
export function calculateRefund(lines: Claims.ClaimResolutionLine[]): number {
  const refundLines = lines.filter(line => isRefundResolution(line.resolutionType));
  if (isEmpty(refundLines)) return 0;

  const shippingLines = lines.filter(
    line => line.lineType === Claims.ClaimResolutionLineType.SHIPPING && line.resolutionType === CUSTOM,
  );

  return +[...refundLines, ...shippingLines].reduce((total, line) => total + line.resolvedAmount, 0).toFixed(2);
}

export function calculateBonusValue(itemLines: ResolutionLineDto[], settings, exchangeRate: number): number {
  if (!settings?.storeCreditBonusType) return 0;

  //This is in store's currency
  if (settings.storeCreditBonusType === 'fixed') {
    return +(settings.storeCreditBonus / exchangeRate).toFixed(2);
  }

  //Order Currency
  const bonusItemValue = itemLines
    .filter(line => isStoreCreditResolution(line.resolutionType))
    .reduce((total, line) => {
      if (line.calculationType === Claims.CalculationType.FLAT) return total + (line.resolvedAmount ?? 0);

      const price = line.claimItem?.price ?? 0;
      const discount = line.claimItem?.discount ?? 0;
      return total + (price - discount) * line.quantity * (line.percentage / 100);
    }, 0)
    .toFixed(2);

  return +(+bonusItemValue * (settings.storeCreditBonus / 100)).toFixed(2);
}

export function isFlatRefund(item): boolean {
  return item.calculationType === Claims.CalculationType.FLAT;
}

export function isFlatResolutionLine(item): boolean {
  return (
    item.line?.lineType === Claims.ClaimResolutionLineType.ITEM &&
    item.line?.calculationType === Claims.CalculationType.FLAT
  );
}

export function isFlatRateResolution(resolutionType: string): boolean {
  return [FLAT_RATE, FLAT_RATE_STORE_CREDIT].includes(resolutionType);
}

export function isStoreCreditResolution(resolutionType: string): boolean {
  return [STORE_CREDIT, FLAT_RATE_STORE_CREDIT].includes(resolutionType);
}

export function isRefundResolution(resolutionType: string): boolean {
  return [REFUND, FLAT_RATE].includes(resolutionType);
}

/**
 * True when a resolution breakdown represents money paid to the customer (a refund or store
 * credit), as opposed to goods (reship/replacement). The amount breakdown is only meaningful to
 * show for payouts — for a reship the total is just the item's reimbursement value, not money the
 * customer receives, so surfaces hide the breakdown when this is false.
 */
export function breakdownHasCustomerPayout(breakdown: { lines: Array<{ resolutionType: string }> }): boolean {
  return breakdown.lines.some(
    line =>
      // FLAT_RATE is always a monetary refund to the customer, never a goods-only reship.
      line.resolutionType === REFUND ||
      line.resolutionType === FLAT_RATE ||
      isStoreCreditResolution(line.resolutionType),
  );
}

export function calculateItemRefund(item): number {
  const amount = item.price - item.discount + item.tax;
  return +(amount * item.quantity * (item.percentage / 100)).toFixed(4);
}

/**
 * Claim items don't have prices
 * We get the prices for the claim item by matching the order item
 *
 * These will be added into the claim item:
 * - selectedOptions
 * - price
 * - tax
 * - discount
 */
export function findMatchingOrderItem(claimItems: Claims.ClaimItem[], orderItems: Orders.OrderItem[]) {
  return claimItems.map(claimItem => {
    let orderItem = orderItems.find(
      item => item.sourceItemId === claimItem.sourceItemId && item.sourceVariantId === claimItem.sourceVariantId,
    );

    if (!orderItem) {
      orderItem = orderItems.find(item => matchesVariant(item, claimItem));
    }

    if (!orderItem) {
      console.warn(
        `Order item not found: variantId: ${claimItem.sourceVariantId}, productId: ${claimItem.sourceProductId}, claimItemId: ${claimItem.sourceItemId}. Using claim item defaults.`,
      );
    }
    // Use order item values if found, otherwise use defaults
    // Defaults are used when adding products outside the original order (e.g., custom reship items)
    return {
      ...claimItem,
      reshipSourceItemId: orderItem?.sourceItemId,
      selectedOptions: orderItem?.selectedOptions || [],
      price: orderItem?.price || 0,
      tax: orderItem?.tax || 0,
      discount: orderItem?.discount || 0,
    } as Claims.ClaimItem & {
      reshipSourceItemId: Orders.OrderItem['sourceItemId'];
      selectedOptions: Orders.OrderItem['selectedOptions'];
      price: Orders.OrderItem['price'];
      tax: Orders.OrderItem['tax'];
      discount: Orders.OrderItem['discount'];
    };
  });
}

export function calculateShippingAmount(data: {
  overrideShippingAmount?: number;
  resolutionType: string;
  draftOrderShippingAmount?: number;
  shippingCost: number;
  shippingTax: number;
}): number {
  if (Number.isFinite(data.overrideShippingAmount)) return data.overrideShippingAmount || 0;
  if (data.resolutionType === Claims.ResolutionType.DRAFT_ORDER && !isEmpty(data.draftOrderShippingAmount)) {
    return data.draftOrderShippingAmount || 0;
  }
  return (data.shippingCost || 0) + (data.shippingTax || 0);
}

// A refund anywhere in the resolution puts shipping on the refund rail, then store credit, else the
// resolution's own type: a mixed record's shipping typed CUSTOM is claimed by no rail and never paid.
export function shippingResolutionTypeFor(itemResolutionTypes: string[], fallback: string): string {
  return [REFUND, STORE_CREDIT].find(type => itemResolutionTypes.includes(type)) ?? fallback;
}

/**
 * The premium an included-in-shipping policy (ENG-6130) built into the order's shipping line, taxed at that line's
 * own rate; every other policy carries none.
 */
export function includedShippingPremium(
  policy: { premiumSource?: string | null; customerAmount?: number | null } | null | undefined,
  shipping: { shippingCost: number; shippingTax: number },
): number {
  if (policy?.premiumSource !== Claims.PremiumSource.SHIPPING_TIER) return 0;

  const taxRate = shipping.shippingCost ? (shipping.shippingTax || 0) / shipping.shippingCost : 0;
  return round2((policy.customerAmount || 0) * (1 + taxRate));
}

/**
 * Shipping still owed once the platform has returned some of it. Refunding what is already refunded
 * funds nothing, so the promise has to price off the remainder or it reads as a shortfall. An included
 * premium was never shipping, so it comes off too.
 *
 * The cost/tax split stops meaning anything once part of the total is gone — only the sum is priced
 * — so the remainder is returned whole and the tax zeroed rather than apportioned between them.
 */
export function remainingShipping(data: {
  shippingCost: number;
  shippingTax: number;
  refunded: number;
  includedPremium?: number;
}): {
  shippingCost: number;
  shippingTax: number;
} {
  const owed = (data.shippingCost || 0) + (data.shippingTax || 0);
  const remaining = Math.max(+(owed - (data.refunded || 0) - (data.includedPremium || 0)).toFixed(2), 0);

  return { shippingCost: remaining, shippingTax: 0 };
}

export function calculateResolvedLines(data: {
  lines: ResolutionLineDto[];
  record: { overrideShippingAmount?: number; resolutionType: string };
  shippingConfig: { draftOrderShippingAmount: number; shippingCost: number; shippingTax: number };
  bonusConfig: {
    settings: Pick<Merchant.GeneralSetting, 'storeCreditBonus' | 'storeCreditBonusType'>;
    storeExchangeRate: number;
  };
}) {
  const { lines, record, shippingConfig, bonusConfig } = data;

  const resolved = lines.map(line => {
    if (line.lineType === Claims.ClaimResolutionLineType.ITEM) {
      if (
        line.calculationType === Claims.CalculationType.FLAT ||
        (line.resolutionType && isFlatRateResolution(line.resolutionType))
      ) {
        return { ...line };
      }

      const resolvedAmount = calculateItemRefund({
        price: line.claimItem?.price,
        discount: line.claimItem?.discount,
        tax: line.claimItem?.tax,
        quantity: line.quantity,
        percentage: line.percentage,
      });
      return { ...line, resolvedAmount };
    }

    if (line.lineType === Claims.ClaimResolutionLineType.SHIPPING) {
      // Only a FLAT line carries an override, matching the ITEM branch above. A FULL line's
      // resolvedAmount is a figure the caller computed — on a reship claim it is priced off the
      // replacement order, which has no shipping — so re-price it from the order in hand.
      const isFlatOverride = line.calculationType === Claims.CalculationType.FLAT;
      const resolvedAmount = calculateShippingAmount({
        overrideShippingAmount: isFlatOverride ? line.resolvedAmount : record.overrideShippingAmount,
        resolutionType: record.resolutionType,
        draftOrderShippingAmount: shippingConfig.draftOrderShippingAmount,
        shippingCost: shippingConfig.shippingCost,
        shippingTax: shippingConfig.shippingTax,
      });
      return { ...line, resolvedAmount };
    }

    if (line.lineType === Claims.ClaimResolutionLineType.CREDIT_BONUS) {
      const itemLines = lines.filter(
        l => l.lineType === Claims.ClaimResolutionLineType.ITEM || l.lineType === Claims.ClaimResolutionLineType.ORDER,
      );
      const resolvedAmount = +calculateBonusValue(
        itemLines,
        bonusConfig.settings,
        bonusConfig.storeExchangeRate,
      ).toFixed(2);
      return { ...line, resolvedAmount };
    }

    return line;
  });

  // A shipping line that *derived* to nothing is dropped rather than carried: it promises nothing a
  // delivery could satisfy, renders as a $0 row the customer is told about, and is what an agent
  // reads when it writes "including shipping" about shipping already refunded.
  //
  // A FLAT 0 is kept. That one is an explicit $0 an agent chose, not an absence.
  return resolved.filter(
    line =>
      !(
        line.lineType === Claims.ClaimResolutionLineType.SHIPPING &&
        !line.resolvedAmount &&
        line.calculationType !== Claims.CalculationType.FLAT
      ),
  );
}

export function getCalculationType(item: {
  resolvedAmount?: number | null;
  percentage?: number;
}): Claims.CalculationType {
  if (item.resolvedAmount) return Claims.CalculationType.FLAT;
  if ((item.percentage ?? 100) < 100) return Claims.CalculationType.PERCENTAGE;
  return Claims.CalculationType.FULL;
}

export function calculateRecordAmount(lines: { creditStore: boolean; resolvedAmount: number }[], creditStore: boolean) {
  return +lines
    .filter(line => line.creditStore === creditStore)
    .reduce((sum, line) => sum + line.resolvedAmount, 0)
    .toFixed(2);
}

interface BreakdownLineInput {
  lineType: Claims.ClaimResolutionLineType;
  resolutionType: string;
  calculationType: Claims.CalculationType;
  quantity: number;
  percentage: number;
  resolvedAmount: number;
  creditStore: boolean;
  coveredAmount?: number;
  claimItem?: { name?: string; price?: number; discount?: number; tax?: number } | null;
}

function round2(n: number): number {
  return +n.toFixed(2);
}

interface ItemPricing {
  price: number;
  discount: number;
  tax: number;
  quantity: number;
  percentage: number;
}

function derivationFormula(pricing: ItemPricing, amount: number): string {
  const { price, discount, tax, quantity, percentage } = pricing;
  return `(${price.toFixed(2)} − ${discount.toFixed(2)} + ${tax.toFixed(2)}) × ${quantity} @ ${percentage}% = ${amount.toFixed(2)}`;
}

// Priced quotes pay PERCENT layers as FLAT at their own %, but a paying 0% line overrides the full value.
export function bufferBaselinePercentage(percentage: number | undefined, resolvedAmount: number): number {
  if (percentage == null) return 100;
  if (percentage === 0 && resolvedAmount > 0) return 100;
  return percentage;
}

function buildBreakdownLine(line: BreakdownLineInput): ResolutionAmountBreakdownLine {
  const price = line.claimItem?.price ?? 0;
  const discount = line.claimItem?.discount ?? 0;
  const tax = line.claimItem?.tax ?? 0;
  const resolvedAmount = round2(line.resolvedAmount ?? 0);
  const coveredAmount = round2(line.coveredAmount ?? 0);

  const base = {
    lineType: line.lineType,
    resolutionType: line.resolutionType,
    calculationType: line.calculationType,
    resolvedAmount,
    coveredAmount,
    creditStore: line.creditStore,
  };

  if (line.lineType !== Claims.ClaimResolutionLineType.ITEM) {
    const labels: Partial<Record<Claims.ClaimResolutionLineType, string>> = {
      [Claims.ClaimResolutionLineType.SHIPPING]: 'Shipping',
      [Claims.ClaimResolutionLineType.CREDIT_BONUS]: 'Store-credit bonus',
    };
    // A flat, agent-issued courtesy credit rides as an ORDER-level STORE_CREDIT line; label it
    // distinctly from the settings-driven CREDIT_BONUS so the breakdown reads clearly.
    const isCourtesyCredit =
      line.lineType === Claims.ClaimResolutionLineType.ORDER && isStoreCreditResolution(line.resolutionType);
    const label = isCourtesyCredit ? 'Courtesy store credit' : (labels[line.lineType] ?? line.lineType);
    // An order-level line that names a product is one, and reads far better as that name than as
    // "ORDER" — or, worse, as a courtesy credit it isn't.
    const description = line.claimItem?.name || label;
    return {
      ...base,
      description,
      basis: null,
      baseValue: resolvedAmount,
      flatOverride: null,
      buffer: null,
      formula: `${description}: ${resolvedAmount.toFixed(2)}`,
    };
  }

  const description = line.claimItem?.name || 'Item';
  const basis = { unitPrice: price, discount, tax, quantity: line.quantity, percentage: line.percentage };

  if (line.calculationType === Claims.CalculationType.FLAT) {
    const percentage = bufferBaselinePercentage(line.percentage, resolvedAmount);
    const pricing = { price, discount, tax, quantity: line.quantity, percentage };
    const baseValue = round2(calculateItemRefund(pricing));
    const buffer = round2(resolvedAmount - baseValue);
    const sign = buffer >= 0 ? '+' : '';
    const bufferFormula = `flat ${resolvedAmount.toFixed(2)} (base ${baseValue.toFixed(2)}, buffer ${sign}${buffer.toFixed(2)})`;
    return {
      ...base,
      description,
      basis,
      baseValue,
      flatOverride: resolvedAmount,
      buffer,
      formula: buffer === 0 ? derivationFormula(pricing, resolvedAmount) : bufferFormula,
    };
  }

  const baseValue = round2(calculateItemRefund({ price, discount, tax, quantity: line.quantity, percentage: 100 }));
  return {
    ...base,
    description,
    basis,
    baseValue,
    flatOverride: null,
    buffer: null,
    formula: derivationFormula(
      { price, discount, tax, quantity: line.quantity, percentage: line.percentage },
      resolvedAmount,
    ),
  };
}

/**
 * The single place a resolution amount is EXPLAINED. Consumes the same matrix-adjusted lines and
 * totals the calculation produced, so the breakdown can never disagree with the persisted amount.
 * Used by both the resolve-time path and the automation dry-run (suggested amount).
 */
export function buildResolutionBreakdown(data: {
  lines: BreakdownLineInput[];
  currency: string;
  totalAmount: number;
  creditedAmount: number;
  notCreditedAmount: number;
}): ResolutionAmountBreakdown {
  const lines = data.lines.map(buildBreakdownLine);
  const total = round2(data.totalAmount);
  const flaggedBuffers = lines
    .filter((l): l is ResolutionAmountBreakdownLine & { buffer: number } => l.buffer != null && l.buffer !== 0)
    .map(l => `${l.description} buffer ${l.buffer >= 0 ? '+' : ''}${l.buffer.toFixed(2)}`);
  const bufferNote = flaggedBuffers.length ? ` Includes ${flaggedBuffers.join(', ')}.` : '';
  const summary =
    `Total ${data.currency} ${total.toFixed(2)} across ${lines.length} line(s): ` +
    lines.map(l => `${l.description} ${l.resolvedAmount.toFixed(2)}`).join(' + ') +
    `.${bufferNote}`;

  return {
    currency: data.currency,
    totalAmount: total,
    creditedAmount: round2(data.creditedAmount),
    notCreditedAmount: round2(data.notCreditedAmount),
    lines,
    summary,
  };
}

export function overrideLineClaimItems(
  record: Claims.ClaimResolutionRecord | null,
  orderItems: Orders.OrderItem[],
): Claims.ClaimResolutionRecord | null {
  if (!record) return null;
  if (isEmpty(orderItems)) return record;

  return {
    ...record,
    lines: record.lines.map(line => {
      if (!line.claimItem) return line;

      const orderItem = orderItems.find(oi => matchesVariant(oi, line.claimItem!));

      if (!orderItem) {
        console.warn(
          `Cannot override line claim item with order item: variantId: ${line.claimItem.sourceVariantId}, productId: ${line.claimItem.sourceProductId}. Using claim item as-is.`,
        );
        return line;
      }

      return {
        ...line,
        claimItem: { ...line.claimItem, sourceItemId: orderItem.sourceItemId },
      };
    }),
  };
}

/**
 * The five `ResolutionType` values a platform service actually delivers. Taken from what
 * shopify-svc, bigcommerce-svc and fluid-svc dispatch on: RESHIP, REFUND, FLAT_RATE and DRAFT_ORDER
 * by name, plus store credit via `isStoreCreditResolution`. Nothing outside this set is delivered by
 * anything, so nothing outside it can be checked.
 */
export const DELIVERY_RESOLUTION_TYPES = [
  Claims.ResolutionType.RESHIP,
  Claims.ResolutionType.REFUND,
  Claims.ResolutionType.FLAT_RATE,
  Claims.ResolutionType.STORE_CREDIT,
  Claims.ResolutionType.DRAFT_ORDER,
] as const;

export type DeliveryResolutionType = (typeof DELIVERY_RESOLUTION_TYPES)[number];

/**
 * Every `ResolutionType`, mapped to the delivery it promises — or `null` when it promises nothing.
 * 21 of the 28 values are denials or closers: expecting a delivery against those would mark every
 * denied claim as undelivered. Exhaustive by construction, so a new `ResolutionType` fails the
 * completeness test rather than silently defaulting either way.
 */
export const RESOLUTION_TYPE_DELIVERY: Record<Claims.ResolutionType, DeliveryResolutionType | null> = {
  // Promises a delivery, under its own name.
  RESHIP: Claims.ResolutionType.RESHIP,
  REFUND: Claims.ResolutionType.REFUND,
  FLAT_RATE: Claims.ResolutionType.FLAT_RATE,
  STORE_CREDIT: Claims.ResolutionType.STORE_CREDIT,
  DRAFT_ORDER: Claims.ResolutionType.DRAFT_ORDER,

  // The same delivery under a different name — `isStoreCreditResolution` covers both.
  FLAT_RATE_STORE_CREDIT: Claims.ResolutionType.STORE_CREDIT,

  // Promises nothing a platform service delivers. `REPLACEMENT`, `EXCHANGE` and `CREDIT` look
  // deliverable but no service dispatches on them, so checking one would report MISSING every
  // time. `CUSTOM` and `RETURN` carry their real instrument on the line's own resolutionType, so
  // the record-level value is not what gets checked.
  REPLACEMENT: null,
  EXCHANGE: null,
  CREDIT: null,
  CUSTOM: null,
  RETURN: null,
  EXPIRED30: null,
  OTHER: null,
  NO_RESPONSE: null,
  PACKAGE_ARRIVED: null,
  CUSTOMER_RESPONDED: null,
  FOLLOW_UP: null,
  INSUFFICIENT_SHIPPER: null,
  RETURN_TO_SENDER: null,
  NOT_COVERED: null,
  ADRESS_TYPO: null,
  PHOTO_VERIFIED: null,
  THRESHOLD_REACHED: null,
  ADDRESS_TYPO: null,
  ALREADY_REFUNDED: null,
  ALREADY_REPLACED: null,
  POTENTIAL_FRAUD: null,
  NOT_MERCHANT_PRODUCT: null,
};

export function resolutionTypeDelivers(resolutionType: string): DeliveryResolutionType | null {
  return RESOLUTION_TYPE_DELIVERY[resolutionType as Claims.ResolutionType] ?? null;
}

/**
 * Groups a resolution's lines by the delivery each one promises, summing `resolvedAmount` per
 * action type. Lines that promise nothing are dropped, so a denial yields an empty map and a
 * mixed record yields only its delivering half.
 */
export function promisedDeliveries(
  lines: Array<{ resolutionType: string; resolvedAmount: number }>,
): Map<DeliveryResolutionType, number> {
  const promised = new Map<DeliveryResolutionType, number>();

  for (const line of lines) {
    const delivery = resolutionTypeDelivers(line.resolutionType);
    if (!delivery) continue;

    promised.set(delivery, +((promised.get(delivery) ?? 0) + line.resolvedAmount).toFixed(2));
  }

  return promised;
}
